import type {
  TBeneficiaries,
  TBeneficiariesAgreement,
  TConvertToCreator,
  TDeleteBeneficiaries,
  TGetBeneficiaries,
  TListMediaFile,
  TmediaMetaData,
  TUserSubscription,
  UserState_req,
  UserState_res,
} from '@/types/user.types';
import { http } from './axios';
import type {
  InAppFeaturePayload,
  InAppFeatureTalktimePayload,
  PersonalityAnswersResponse,
  QuestionsResponse,
  TBuyProductRes,
  TprofilePicture,
} from '@/types/common.types';
import axios from 'axios';
import type { TTalkTimeCredits } from '@/types/digital-twin.types';
import { consoleError } from '@/utils/common-utils';

const apiUrl_old = import.meta.env.VITE_BACKEND_URL_OLD;

export const getUserEntitlements = async (): Promise<TUserSubscription> => {
  try {
    const response = await http.get<TUserSubscription>('/v1/stripe_payment/get-user-entitlements', {});
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const getUserState = async (): Promise<Array<UserState_res>> => {
  try {
    const response = await http.get<Array<UserState_res>>('/v1/user_progress/', {});
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const postUserState = async (payload: UserState_req): Promise<void> => {
  try {
    await http.post<Array<UserState_req>>('/v1/user_progress/', payload);
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const personalityAnswer = async (userId: string, payload: any[]): Promise<void> => {
  try {
    await http.post<any[]>(`/v1/personality/answer/?user_id=${userId}`, payload, {
      baseURL: apiUrl_old,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const fetchPersonalityQuestions = async (stage: number = 20): Promise<QuestionsResponse> => {
  try {
    const response = await http.get<QuestionsResponse>(`/v1/question/?stage=${stage}`, {});
    return response.data;
  } catch (error) {
    consoleError('Failed to fetch personality questions:', error);
    return {
      stage_information: {
        stage_name: '',
        total_stage_questions: 0,
      },
      questions: [],
    };
  }
};

export const fetchPersonalityAnswers = async (stage: number = 20): Promise<PersonalityAnswersResponse> => {
  try {
    const response = await http.get<PersonalityAnswersResponse>(`/v1/personality/answers/?stage_id=${stage}`, {
      baseURL: apiUrl_old,
    });
    return response.data;
  } catch (error) {
    consoleError('Failed to fetch personality answers:', error);
    return {
      stage_id: stage,
      user_id: '',
      answers_by_question_id: {},
      answers_by_question_number: {},
    };
  }
};

export const updateMediaMetadata = async (metadata: { [key: string]: TmediaMetaData }) => {
  try {
    await http.put(`/v1/creator/media_files`, metadata);
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const getMediaFiles = async (): Promise<TListMediaFile> => {
  try {
    const response = await http.get<TListMediaFile>('/v1/creator/media_files', {});
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const deleteMediaFile = async (mediaFileId: string) => {
  try {
    await http.delete(`/v1/creator/media_files/${mediaFileId}`);
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const uploadFileToCloudService = async (
  file: File,
  onUploadProgress: (progress: number) => void
): Promise<{ media_path: string; id: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  return http
    .post('/v1/creator/media_files', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: event => {
        if (event.lengthComputable && typeof onUploadProgress === 'function' && event.total) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onUploadProgress(progress);
        }
      },
    })
    .then(response => {
      const { uri, media_id } = response.data;
      return { media_path: uri, id: media_id };
    });
};

export const uploadProfilePicture = async (file: File): Promise<{ media_path: string; id: string } | null> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('profile_picture', 'true');

  try {
    const response = await http.post('/v1/creator/media_files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    const { url, media_id } = response.data;
    return { media_path: url, id: media_id };
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const getProfilePicture = async (): Promise<TprofilePicture> => {
  try {
    const response = await http.get<TprofilePicture>(`/v1/creator/profile_picture`, {});
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const getBeneficiaries = async (): Promise<TGetBeneficiaries> => {
  try {
    const response = await http.get<TGetBeneficiaries>(`/v1/beneficiaries`, {});
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const submitLifetimeBeneficiariesAgreement = async (payload: TBeneficiariesAgreement[]) => {
  try {
    const response = await http.post('/v1/sign_beyond_lifetime_beneficiaries_agreement', payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 400 && error.response.data?.message) {
        throw new Error(error.response.data.message);
      }
    }
    throw new Error('Failed to submit agreement. Please try again.');
  }
};

export const getBeneficiarieByID = async (ID: string, key: string = 'uid'): Promise<TBeneficiaries> => {
  const payload = {
    [key]: ID,
  };
  try {
    const response = await http.post<TBeneficiaries>(`/v1/beneficiaries`, payload, {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const shareToBeneficiaries = async (payload: string[]) => {
  try {
    await http.post<void>(`/v1/beneficiaries/share`, payload);
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const buyProduct = async (request: InAppFeaturePayload): Promise<TBuyProductRes> => {
  try {
    const response = await http.post<TBuyProductRes>(`/v1/stripe_payment/create-checkout-session`, request, {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const buyProductTalkTime = async (request: InAppFeatureTalktimePayload): Promise<TBuyProductRes> => {
  try {
    const response = await http.post<TBuyProductRes>(`/v1/stripe_payment/create-bulk-checkout-session`, request, {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const deleteUser = async () => {
  try {
    await http.post<void>(`/v1/admin/delete_user`, {});
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const convertToCreator = async (payload: TConvertToCreator) => {
  try {
    await http.post<void>(`/v1/convert-to-creator`, payload);
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const deleteBeneficiary = async (deleteBeneficiarie: TDeleteBeneficiaries) => {
  try {
    await http.delete(`/v1/delete_beneficiary`, {
      baseURL: apiUrl_old,
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      data: deleteBeneficiarie,
    });
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const getRemainingTalkTimeCredits = async (): Promise<TTalkTimeCredits> => {
  try {
    const response = await http.get<TTalkTimeCredits>(`/v1/stripe_payment/get-remaining-talk-time-credits`, {});
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};
